import { t, useLanguage } from '../../i18n';
import React, { useState } from 'react';
import { View, StyleSheet, ScrollView, TouchableOpacity, Alert, Platform, Text } from 'react-native';
import { useTheme, FAB, Portal, Modal, TextInput, Button } from 'react-native-paper';
import { useSchedule, ScheduleTemplate } from '../../contexts/ScheduleContext';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';

const SavedSchedulesScreen = ({ navigation }: any) => {
  useLanguage();
    const { savedTemplates, deleteTemplate, saveCurrentAsTemplate, loadTemplate, reorderTemplates } = useSchedule() as any;
    const [saveModalVisible, setSaveModalVisible] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');
    const [exportingId, setExportingId] = useState<string | null>(null);
    const theme = useTheme();

    const handleSaveCurrent = async () => {
        if (!newTemplateName.trim()) {
            if (Platform.OS === 'web') {
                window.alert(t("名前を入力してください"));
            } else {
                Alert.alert(t("エラー"), t("名前を入力してください"));
            }
            return;
        }
        await saveCurrentAsTemplate(newTemplateName.trim());
        setNewTemplateName('');
        setSaveModalVisible(false);
    };

    const handleLoad = async (template: ScheduleTemplate) => {
        const doLoad = async () => {
            await loadTemplate(template.id);
            if (navigation && navigation.navigate) {
                navigation.navigate('Schedule');
            }
        };

        if (Platform.OS === 'web') {
            if (window.confirm(t("「{0}」を現在の時間割として適用しますか？\n現在の未保存の変更は失われます。", [template.name]))) {
                await doLoad();
            }
        } else {
            Alert.alert(
                t("時間割の切替"),
                t("「{0}」を現在の時間割として適用しますか？現在の未保存の変更は失われます。", [template.name]),
                [
                    { text: t("キャンセル"), style: 'cancel' },
                    { text: t("現在の時間割として適用"), onPress: doLoad }
                ]
            );
        }
    };

    const handleDelete = (template: ScheduleTemplate) => {
        const doDelete = () => deleteTemplate(template.id);

        if (Platform.OS === 'web') {
            if (window.confirm(t("「{0}」を削除してもよろしいですか？", [template.name]))) {
                doDelete();
            }
        } else {
            Alert.alert(
                t("削除の確認"),
                t("「{0}」を削除してもよろしいですか？", [template.name]),
                [
                    { text: t("キャンセル"), style: 'cancel' },
                    { text: t("削除"), onPress: doDelete, style: 'destructive' }
                ]
            );
        }
    };

    const formatDate = (timestamp: number) => {
        const d = new Date(timestamp);
        return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`;
    };

    const handleExport = async (template: ScheduleTemplate) => {
        if (Platform.OS !== 'web') {
            navigation.navigate('SchedulePreview', { templateId: template.id });
            return;
        }
        try {
            setExportingId(template.id);
            const element = document.getElementById(`timetable-export-${template.id}`);
            if (!element) {
                navigation.navigate('SchedulePreview', { templateId: template.id });
                return;
            }
            const html2canvas = require('html2canvas');
            const canvas = await html2canvas(element, {
                backgroundColor: '#ffffff',
                scale: 2,
            });
            const link = document.createElement('a');
            link.download = `tufs-timetable-${template.name}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (error) {
            console.error('画像書き出しエラー:', error);
            navigation.navigate('SchedulePreview', { templateId: template.id });
        } finally {
            setExportingId(null);
        }
    };

    const renderList = () => {
        if (savedTemplates.length === 0) {
            return (
                <View style={styles.emptyContainer}>
                    <View style={[styles.emptyIconWrap, { backgroundColor: theme.colors.surfaceVariant }]}>
                        <MaterialCommunityIcons name="folder-open" size={48} color={theme.colors.onSurfaceVariant} />
                    </View>
                    <Text style={[styles.emptyTitle, { color: theme.colors.onSurface }]}>
                        {t("保存された時間割はありません")}
                    </Text>
                    <Text style={[styles.emptySubtitle, { color: theme.colors.onSurfaceVariant }]}>
                        {t("下のボタンから現在の時間割を保存できます")}
                    </Text>
                </View>
            );
        }


        // 統合されたリスト表示
        return savedTemplates.map((template: any, index: number) => (
            <View key={template.id} style={[styles.item, { backgroundColor: theme.colors.surface }]}>
                <TouchableOpacity
                    style={styles.itemMain}
                    onPress={() => navigation.navigate('SchedulePreview', { templateId: template.id })}
                    activeOpacity={0.7}
                >
                    <View style={[styles.indexBadge, { borderColor: theme.colors.primary }]}>
                        <Text style={[styles.indexBadgeText, { color: theme.colors.primary }]}>{index + 1}</Text>
                    </View>

                    <View style={styles.itemInfo}>
                        <Text style={[styles.itemName, { color: theme.colors.onSurface }]} numberOfLines={1}>
                            {template.name}
                        </Text>
                        <View style={styles.metaRow}>
                            <MaterialCommunityIcons name="calendar" size={12} color={theme.colors.onSurfaceVariant} />
                            <Text style={[styles.itemMeta, { color: theme.colors.onSurfaceVariant }]}>
                                {' '}{formatDate(template.updatedAt)}
                            </Text>
                            {template.courses && (
                                <>
                                    <Text style={[styles.metaSep, { color: theme.colors.onSurfaceVariant }]}> · </Text>
                                    <MaterialCommunityIcons name="book" size={12} color={theme.colors.onSurfaceVariant} />
                                    <Text style={[styles.itemMeta, { color: theme.colors.onSurfaceVariant }]}>
                                        {' '}{t('{0} 科目', [template.courses.length])}
                                    </Text>
                                </>
                            )}
                        </View>
                    </View>

                    <View style={styles.inlineActions}>
                        <TouchableOpacity
                            style={styles.iconActionBtn}
                            onPress={() => handleLoad(template)}
                            activeOpacity={0.7}
                        >
                            <MaterialCommunityIcons name="check-circle-outline" size={26} color={theme.colors.primary} />
                        </TouchableOpacity>

                        <TouchableOpacity
                            style={styles.iconActionBtn}
                            onPress={() => handleDelete(template)}
                            activeOpacity={0.7}
                        >
                            <MaterialCommunityIcons name="trash-can-outline" size={24} color={theme.colors.error} />
                        </TouchableOpacity>
                    </View>
                </TouchableOpacity>
            </View>
        ));
    };

    return (
        <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
            {/* ヘッダー */}
            <View style={styles.header}>
                <Text style={styles.headerTitle}>{t("保存済み時間割")}</Text>
                <View style={styles.headerBadge}>
                    <Text style={styles.headerBadgeText}>{savedTemplates.length}</Text>
                </View>
            </View>

            <ScrollView contentContainerStyle={styles.listContent} showsVerticalScrollIndicator={false}>
                {renderList()}
            </ScrollView>

            <FAB
                icon={() => <MaterialCommunityIcons name="plus" size={24} color="#fff" />}
                style={styles.fab}
                color="#fff"
                onPress={() => setSaveModalVisible(true)}
                label={t("現在の時間割を保存")}
            />

            <Portal>
                <Modal
                    visible={saveModalVisible}
                    onDismiss={() => setSaveModalVisible(false)}
                    contentContainerStyle={[styles.modal, { backgroundColor: theme.colors.elevation.level3 }]}
                >
                    <View style={styles.modalHeader}>
                        <MaterialCommunityIcons name="content-save" size={24} color="#e75480" />
                        <Text style={[styles.modalTitle, { color: theme.colors.onSurface }]}>{t("時間割を保存")}</Text>
                    </View>
                    <TextInput
                        label={t("スケジュール名")}
                        value={newTemplateName}
                        onChangeText={setNewTemplateName}
                        mode="outlined"
                        style={styles.input}
                        placeholder={t("例: 2026年度春学期")}
                        outlineColor={theme.colors.outline}
                        activeOutlineColor={theme.colors.onSurface}
                    />
                    <View style={styles.modalActions}>
                        <Button
                            onPress={() => setSaveModalVisible(false)}
                            textColor={theme.colors.onSurfaceVariant}
                        >
                            {t("キャンセル")}
                        </Button>
                        <Button
                            mode="contained"
                            onPress={handleSaveCurrent}
                            buttonColor="#e75480"
                            style={{ borderRadius: 8 }}
                        >
                            {t("保存する")}
                        </Button>
                    </View>
                </Modal>
            </Portal>
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1 },
    header: {
        backgroundColor: '#e75480',
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 14,
        gap: 8,
    },
    headerTitle: {
        fontSize: 20,
        fontWeight: 'bold',
        color: '#fff',
        flex: 1,
    },
    headerBadge: {
        backgroundColor: 'rgba(255,255,255,0.25)',
        borderRadius: 12,
        paddingHorizontal: 10,
        paddingVertical: 2,
    },
    headerBadgeText: {
        color: '#fff',
        fontSize: 13,
        fontWeight: 'bold',
    },
    listContent: {
        padding: 16,
        paddingBottom: 110,
        gap: 12,
    },
    // ── アイテム ──────────────────────────────
    item: {
        borderRadius: 14,
        overflow: 'hidden',
        elevation: 2,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.08,
        shadowRadius: 4,
    },
    itemMain: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 14,
        gap: 16,
    },
    indexBadge: {
        width: 32,
        height: 32,
        borderRadius: 16,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'transparent',
        borderWidth: 1.5,
        flexShrink: 0,
    },
    indexBadgeText: {
        fontSize: 13,
        fontWeight: 'bold',
    },
    itemInfo: {
        flex: 1,
        gap: 4,
    },
    itemName: {
        fontSize: 16,
        fontWeight: '600',
    },
    metaRow: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    itemMeta: {
        fontSize: 12,
    },
    metaSep: {
        fontSize: 12,
    },
    inlineActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        marginLeft: 8,
    },
    iconActionBtn: {
        padding: 4,
        justifyContent: 'center',
        alignItems: 'center',
    },
    // ── 空 ──────────────────────────────────
    emptyContainer: {
        alignItems: 'center',
        marginTop: 80,
        paddingHorizontal: 32,
        gap: 12,
    },
    emptyIconWrap: {
        width: 96,
        height: 96,
        borderRadius: 48,
        justifyContent: 'center',
        alignItems: 'center',
        marginBottom: 8,
    },
    emptyTitle: {
        fontSize: 16,
        fontWeight: '600',
        textAlign: 'center',
    },
    emptySubtitle: {
        fontSize: 13,
        textAlign: 'center',
        lineHeight: 20,
    },
    // ── FAB ─────────────────────────────────
    fab: {
        position: 'absolute',
        right: 16,
        bottom: 16,
        backgroundColor: '#e75480',
    },
    // ── モーダル ─────────────────────────────
    modal: {
        margin: 24,
        borderRadius: 16,
        padding: 24,
    },
    modalHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 20,
    },
    modalTitle: {
        fontSize: 18,
        fontWeight: 'bold',
    },
    input: {
        marginBottom: 20,
        backgroundColor: 'transparent',
    },
    modalActions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: 12,
    },
});

export default SavedSchedulesScreen;
